import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Clock, Calendar as CalendarIcon, UserPlus, Check, X, Trash2, Eye, EyeOff, KeyRound } from 'lucide-react';
import api from '../utils/api';
import { useConfirm } from '../context/ConfirmContext';
import { formatDate, getRoleLabel } from '../utils/helpers';
import { toast } from 'sonner';

const locales = { 'it': it };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export const PersonnelPage = () => {
  const { user, hasRole } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [timesheetDialogOpen, setTimesheetDialogOpen] = useState(false);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  
  // Forms
  const [currentUser, setCurrentUser] = useState(null);
  // Reset password state (admin edit dialog)
  const [resetPwd, setResetPwd] = useState('');
  const [resetPwdSent, setResetPwdSent] = useState(false);
  const [resetPwdVisible, setResetPwdVisible] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [timesheetEntry, setTimesheetEntry] = useState({
    user_id: '',
    date: new Date().toISOString().split('T')[0],
    hours: 8,
    description: ''
  });
  const [absenceEntry, setAbsenceEntry] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    absence_type: 'VACATION',
    reason: ''
  });

  const absenceTypes = {
    VACATION: 'Vacanze',
    SICK: 'Malattia',
    INJURY: 'Infortunio',
    MILITARY: 'Militare',
    OTHER: 'Altro'
  };

  const absenceColors = {
    VACATION: '#10b981',
    SICK: '#f59e0b',
    INJURY: '#ef4444',
    MILITARY: '#6366f1',
    OTHER: '#8b5cf6'
  };

  useEffect(() => {
    if (hasRole(['ADMIN', 'MANAGER'])) {
      loadUsers();
    }
    loadTimesheets();
    loadAbsences();
    
    // Set default user_id for forms
    if (user) {
      setTimesheetEntry(prev => ({ ...prev, user_id: user.id }));
      setAbsenceEntry(prev => ({ ...prev, user_id: user.id }));
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimesheets = async () => {
    try {
      const response = await api.getTimesheets({});
      setTimesheets(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento timesheet');
    }
  };

  const loadAbsences = async () => {
    try {
      const response = await api.getAbsences({});
      setAbsences(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento assenze');
    }
  };

  const getUserName = (userId) => {
    const u = users.find(usr => usr.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : 'Utente';
  };

  const formatApiError = (err, fallback = 'Errore') => {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // FastAPI 422 validation errors: array of {loc, msg, type, ...}
      return detail.map(e => e?.msg || 'Errore di validazione').join(' • ');
    }
    return fallback;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Strip fields not applicable to each account type to avoid backend validation errors
      const payload = { ...currentUser };
      if (payload.is_shared) {
        delete payload.email;
        delete payload.last_name;
        payload.role = 'SHARED';
      } else {
        delete payload.username;
        delete payload.is_shared;
      }
      await api.register(payload);
      toast.success('Utente creato');
      setUserDialogOpen(false);
      setCurrentUser(null);
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Errore nella creazione'));
    }
  };

  const handleCreateTimesheet = async (e) => {
    e.preventDefault();
    try {
      await api.createTimesheet(timesheetEntry);
      toast.success('Ore registrate');
      setTimesheetDialogOpen(false);
      setTimesheetEntry({
        user_id: user?.id || '',
        date: new Date().toISOString().split('T')[0],
        hours: 8,
        description: ''
      });
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    }
  };

  const handleApproveTimesheet = async (id) => {
    try {
      await api.approveTimesheet(id);
      toast.success('Timesheet approvato');
      loadTimesheets();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleDeleteTimesheet = async (id) => {
    const ok = await confirm({
      title: 'Elimina registrazione ore',
      description: 'Stai per eliminare definitivamente questa registrazione ore.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteTimesheet(id);
      toast.success('Timesheet eliminato');
      loadTimesheets();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCreateAbsence = async (e) => {
    e.preventDefault();
    try {
      await api.createAbsence(absenceEntry);
      toast.success('Assenza registrata');
      setAbsenceDialogOpen(false);
      setAbsenceEntry({
        user_id: user?.id || '',
        start_date: '',
        end_date: '',
        absence_type: 'VACATION',
        reason: ''
      });
      loadAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione assenza');
      console.error('Absence creation error:', error.response?.data);
    }
  };

  const handleApproveAbsence = async (id) => {
    try {
      await api.approveAbsence(id);
      toast.success('Assenza approvata');
      loadAbsences();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleRejectAbsence = async (id) => {
    try {
      await api.rejectAbsence(id);
      toast.success('Assenza rifiutata');
      loadAbsences();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    }
  };

  const handleDeleteUser = async (userId, userRole) => {
    // Check if it's the last admin
    if (userRole === 'ADMIN') {
      const adminCount = users.filter(u => u.role === 'ADMIN' && u.active).length;
      if (adminCount <= 1) {
        toast.error('Impossibile eliminare: deve esserci almeno un amministratore attivo');
        return;
      }
    }

    const ok = await confirm({
      title: 'Elimina utente',
      description: 'Stai per eliminare definitivamente questo utente. Se ha dati storici (bollettini, fatture, ore) sarà invece sospeso per preservare l\'archivio.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;

    try {
      const res = await api.deleteUser(userId);
      if (res.data?.soft_delete) {
        toast.success('Utente disattivato (ha dati storici associati)');
      } else {
        toast.success('Utente eliminato');
      }
      setUserDialogOpen(false);
      setCurrentUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const { password, ...updateData } = currentUser;
      await api.updateUser(currentUser.id, updateData);
      toast.success('Utente aggiornato');
      setUserDialogOpen(false);
      setCurrentUser(null);
      setResetPwd(''); setResetPwdSent(false); setResetPwdVisible(false);
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Errore nell\'aggiornamento'));
    }
  };

  const handleResetPassword = async () => {
    if (!currentUser?.id) return;
    if (!resetPwd || resetPwd.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    setResettingPwd(true);
    try {
      const res = await api.resetUserPassword(currentUser.id, resetPwd);
      setResetPwdSent(true);
      // Reveal the password automatically the first time after reset so admin can see it
      setResetPwdVisible(true);
      const where = [];
      if (res.data?.email_sent_to_admin) where.push('al tuo indirizzo admin');
      if (res.data?.email_sent_to_user) where.push("all'utente");
      toast.success(`Password reimpostata. Email inviata${where.length ? ` ${where.join(' e ')}` : ''} (modalità test).`);
    } catch (error) {
      toast.error(formatApiError(error, 'Errore nel reset password'));
    } finally {
      setResettingPwd(false);
    }
  };

  // Calendar events from absences (exclude REJECTED)
  const calendarEvents = absences
    .filter(absence => absence.status !== 'REJECTED')
    .map(absence => {
      const userName = getUserName(absence.user_id);
      const statusLabel = absence.status === 'PENDING' ? ' (richiesta)' : '';
      return {
        id: absence.id,
        title: `${absenceTypes[absence.absence_type]} - ${userName}${statusLabel}`,
        start: new Date(absence.start_date),
        end: new Date(absence.end_date),
        resource: absence,
        style: {
          backgroundColor: absence.status === 'PENDING' 
            ? '#94a3b8' // Gray for pending
            : absenceColors[absence.absence_type]
        }
      };
    });

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.style.backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  return (
    <div data-testid="personnel-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Personale</h1>
        <p className="text-slate-600">Gestisci utenti, ore di lavoro e assenze</p>
      </div>

      <Tabs defaultValue={hasRole(['ADMIN', 'MANAGER']) ? 'users' : 'timesheets'} className="space-y-6">
        <TabsList>
          {hasRole(['ADMIN', 'MANAGER']) && <TabsTrigger value="users">Utenti</TabsTrigger>}
          <TabsTrigger value="timesheets">Ore di Lavoro</TabsTrigger>
          <TabsTrigger value="absences">Assenze</TabsTrigger>
        </TabsList>

        {/* USERS TAB */}
        {hasRole(['ADMIN', 'MANAGER']) && (
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gestione Utenti</CardTitle>
                {hasRole(['ADMIN', 'SECRETARY']) && (
                  <Dialog open={userDialogOpen} onOpenChange={(open) => {
                    setUserDialogOpen(open);
                    if (!open) {
                      setResetPwd(''); setResetPwdSent(false); setResetPwdVisible(false);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button onClick={() => setCurrentUser({
                        email: '',
                        username: '',
                        password: '',
                        first_name: '',
                        last_name: '',
                        role: 'EMPLOYEE',
                        is_shared: false,
                      })} data-testid="create-user-btn">
                        <UserPlus size={18} className="mr-2" />
                        Nuovo Utente
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{currentUser?.id ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={currentUser?.id ? handleUpdateUser : handleCreateUser} className="space-y-4" autoComplete="off">
                        {!currentUser?.id && (
                          <label className="flex items-start gap-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition" data-testid="shared-account-toggle-row">
                            <input
                              type="checkbox"
                              checked={!!currentUser?.is_shared}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setCurrentUser({
                                  ...currentUser,
                                  is_shared: checked,
                                  role: checked ? 'SHARED' : 'EMPLOYEE',
                                  // Clear/reset opposite fields
                                  ...(checked ? { email: '', last_name: '' } : { username: '' }),
                                });
                              }}
                              className="mt-0.5 h-4 w-4 accent-amber-600"
                              data-testid="shared-account-checkbox"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-slate-900">Account Condiviso</div>
                              {currentUser?.is_shared && (
                                <p className="text-xs text-slate-600 mt-1.5" data-testid="shared-account-description">
                                  Account dispositivo dedicato — accesso con username (no email). Gli operai selezioneranno il proprio nome al momento di compilare il bollettino.
                                </p>
                              )}
                            </div>
                          </label>
                        )}
                        {currentUser?.is_shared ? (
                          <div className="space-y-2">
                            <Label htmlFor="kiosk_name">Nome account *</Label>
                            <Input
                              id="kiosk_name"
                              value={currentUser?.first_name || ''}
                              onChange={(e) => setCurrentUser({ ...currentUser, first_name: e.target.value })}
                              placeholder="es. Tablet Progetto Nord"
                              required
                              data-testid="kiosk-name-input"
                            />
                            <p className="text-xs text-slate-500">Es. &quot;Tablet Ufficio&quot;, &quot;iPad Progetto Lugano&quot;.</p>
                          </div>
                        ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="first_name">Nome *</Label>
                            <Input
                              id="first_name"
                              value={currentUser?.first_name || ''}
                              onChange={(e) => setCurrentUser({ ...currentUser, first_name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="last_name">Cognome *</Label>
                            <Input
                              id="last_name"
                              value={currentUser?.last_name || ''}
                              onChange={(e) => setCurrentUser({ ...currentUser, last_name: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        )}
                        {currentUser?.is_shared ? (
                          <div className="space-y-2">
                            <Label htmlFor="username">Username *</Label>
                            <Input
                              id="username"
                              value={currentUser?.username || ''}
                              onChange={(e) => setCurrentUser({ ...currentUser, username: e.target.value })}
                              placeholder="es. tablet-ufficio"
                              required
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              autoComplete="off"
                              data-testid="kiosk-username-input"
                            />
                            <p className="text-xs text-slate-500">Solo lettere/numeri. Verrà normalizzato (minuscole, trattini).</p>
                          </div>
                        ) : (
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={currentUser?.email || ''}
                            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
                            required
                            autoComplete="off"
                            data-testid="user-email-input"
                          />
                          {currentUser?.id && (
                            <p className="text-xs text-slate-500">Modificando l&apos;email l&apos;utente dovrà accedere con la nuova.</p>
                          )}
                        </div>
                        )}
                        {!currentUser?.id && (
                          <div className="space-y-2">
                            <Label htmlFor="password">Password *</Label>
                            <Input
                              id="password"
                              type="password"
                              value={currentUser?.password || ''}
                              onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
                              required
                              autoComplete="new-password"
                              data-testid="new-user-password"
                            />
                          </div>
                        )}
                        {!currentUser?.is_shared && (
                        <div className="space-y-2">
                          <Label htmlFor="role">Ruolo *</Label>
                          <Select
                            value={currentUser?.role || 'EMPLOYEE'}
                            onValueChange={(value) => setCurrentUser({ ...currentUser, role: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ADMIN">Amministratore</SelectItem>
                              <SelectItem value="MANAGER">Responsabile</SelectItem>
                              <SelectItem value="SECRETARY">Segretaria</SelectItem>
                              <SelectItem value="EMPLOYEE">Impiegato</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        )}
                        {currentUser?.id && (
                          <div className="space-y-2">
                            <Label htmlFor="user_status">Stato *</Label>
                            <Select
                              value={(currentUser?.active === false) ? 'suspended' : 'active'}
                              onValueChange={(value) => setCurrentUser({ ...currentUser, active: value === 'active' })}
                            >
                              <SelectTrigger data-testid="user-status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Attivo</SelectItem>
                                <SelectItem value="suspended">Sospeso</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">Gli utenti sospesi non possono accedere al sistema</p>
                          </div>
                        )}
                        {currentUser?.id && (
                          <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid="reset-password-block">
                            <div className="flex items-center gap-2">
                              <KeyRound className="h-4 w-4 text-slate-500" />
                              <Label className="text-sm font-medium">Reimposta password</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Input
                                  type={resetPwdVisible ? 'text' : 'password'}
                                  value={resetPwd}
                                  onChange={(e) => { setResetPwd(e.target.value); setResetPwdSent(false); }}
                                  placeholder="Nuova password (min. 8 caratteri)"
                                  className="pr-10"
                                  autoComplete="new-password"
                                  data-testid="reset-password-input"
                                />
                                <button
                                  type="button"
                                  onClick={() => setResetPwdVisible((v) => !v)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900"
                                  aria-label={resetPwdVisible ? 'Nascondi password' : 'Mostra password'}
                                  data-testid="reset-password-toggle-visibility"
                                >
                                  {resetPwdVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleResetPassword}
                                disabled={resettingPwd || !resetPwd}
                                data-testid="reset-password-btn"
                              >
                                {resettingPwd ? 'Invio…' : (resetPwdSent ? 'Reinvia' : 'Reimposta e invia')}
                              </Button>
                            </div>
                            <p className="text-[11px] text-slate-500">
                              {resetPwdSent
                                ? 'Email inviata (modalità test — verrà attivata l\u2019invio reale a integrazione completata).'
                                : (currentUser?.is_shared
                                    ? "L'account kiosk non ha email: la nuova password verrà inviata al tuo indirizzo admin."
                                    : "La nuova password verrà inviata via email all'utente e, in copia, al tuo indirizzo admin.")}
                            </p>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2">
                          {currentUser?.id ? (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDeleteUser(currentUser.id, currentUser.role)}
                              className="text-destructive border-destructive/30 hover:bg-destructive/10"
                              data-testid="dialog-delete-user-btn"
                            >
                              <Trash2 size={16} className="mr-2" />
                              Elimina
                            </Button>
                          ) : <span />}
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                              Annulla
                            </Button>
                            <Button type="submit">
                              {currentUser?.id ? 'Aggiorna' : 'Crea'} Utente
                            </Button>
                          </div>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-600">Nessun utente trovato</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary border-b">
                        <tr>
                          <th className="text-left p-4 text-sm font-semibold text-slate-700">Nome</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-700">Email</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-700">Ruolo</th>
                          <th className="text-center p-4 text-sm font-semibold text-slate-700">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr
                            key={u.id}
                            className={`border-b hover:bg-secondary ${hasRole(['ADMIN']) ? 'cursor-pointer' : ''}`}
                            onClick={hasRole(['ADMIN']) ? () => { setCurrentUser(u); setUserDialogOpen(true); } : undefined}
                            data-testid={`user-row-${u.id}`}
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{u.first_name} {u.last_name}</span>
                                {u.is_platform_admin && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300">
                                    SUPPORTO
                                  </span>
                                )}
                                {(u.is_shared || u.role === 'SHARED') && (
                                  <span
                                    className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                                    data-testid={`shared-badge-${u.id}`}
                                  >
                                    CONDIVISO
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-slate-600">{u.email || (u.username ? `@${u.username}` : '-')}</td>
                            <td className="p-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                (u.is_shared || u.role === 'SHARED')
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {getRoleLabel(u.role)}
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              {u.active ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  Attivo
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  Sospeso
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* TIMESHEETS TAB */}
        <TabsContent value="timesheets">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Ore di Lavoro</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Registra e gestisci le ore lavorate</p>
              </div>
              <Dialog open={timesheetDialogOpen} onOpenChange={setTimesheetDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-timesheet-btn">
                    <Clock size={18} className="mr-2" />
                    Registra Ore
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registra Ore di Lavoro</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTimesheet} className="space-y-4">
                    {hasRole(['ADMIN', 'MANAGER', 'SECRETARY']) && (
                      <div className="space-y-2">
                        <Label htmlFor="user_select">Utente *</Label>
                        <Select
                          value={timesheetEntry.user_id}
                          onValueChange={(value) => setTimesheetEntry({ ...timesheetEntry, user_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="date">Data *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={timesheetEntry.date}
                        onChange={(e) => setTimesheetEntry({ ...timesheetEntry, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hours">Ore *</Label>
                      <Input
                        id="hours"
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={timesheetEntry.hours}
                        onChange={(e) => setTimesheetEntry({ ...timesheetEntry, hours: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descrizione Attività *</Label>
                      <Textarea
                        id="description"
                        value={timesheetEntry.description}
                        onChange={(e) => setTimesheetEntry({ ...timesheetEntry, description: e.target.value })}
                        rows={3}
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setTimesheetDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit">Salva</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {timesheets.length === 0 ? (
                <div className="text-center py-12">
                  <Clock size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600">Nessuna registrazione ore</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary border-b">
                      <tr>
                        {hasRole(['ADMIN', 'MANAGER', 'SECRETARY']) && (
                          <th className="text-left p-4 text-sm font-semibold text-slate-700">Utente</th>
                        )}
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Data</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-700">Ore</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Descrizione</th>
                        <th className="text-center p-4 text-sm font-semibold text-slate-700">Stato</th>
                        {hasRole(['ADMIN', 'MANAGER']) && (
                          <th className="text-right p-4 text-sm font-semibold text-slate-700">Azioni</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {timesheets.map(ts => (
                        <tr key={ts.id} className="border-b hover:bg-secondary">
                          {hasRole(['ADMIN', 'MANAGER', 'SECRETARY']) && (
                            <td className="p-4">{getUserName(ts.user_id)}</td>
                          )}
                          <td className="p-4">{formatDate(ts.date)}</td>
                          <td className="p-4 text-right font-mono font-medium">{ts.hours}h</td>
                          <td className="p-4 text-slate-600">{ts.description}</td>
                          <td className="p-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                              ts.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                              ts.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {ts.status === 'APPROVED' ? 'Approvato' : 
                               ts.status === 'SUBMITTED' ? 'In Revisione' : 'Bozza'}
                            </span>
                          </td>
                          {hasRole(['ADMIN', 'MANAGER']) && (
                            <td className="p-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {ts.status === 'SUBMITTED' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleApproveTimesheet(ts.id)}
                                    className="text-green-600 hover:bg-green-50"
                                  >
                                    <Check size={16} />
                                  </Button>
                                )}
                                {ts.status === 'DRAFT' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteTimesheet(ts.id)}
                                    className="text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABSENCES TAB */}
        <TabsContent value="absences">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gestione Assenze</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Calendario assenze e richieste</p>
              </div>
              <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-absence-btn">
                    <CalendarIcon size={18} className="mr-2" />
                    Nuova Assenza
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registra Assenza</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateAbsence} className="space-y-4">
                    {hasRole(['ADMIN', 'SECRETARY']) && (
                      <div className="space-y-2">
                        <Label htmlFor="absence_user">Utente *</Label>
                        <Select
                          value={absenceEntry.user_id}
                          onValueChange={(value) => setAbsenceEntry({ ...absenceEntry, user_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="absence_type">Tipo Assenza *</Label>
                      <Select
                        value={absenceEntry.absence_type}
                        onValueChange={(value) => setAbsenceEntry({ ...absenceEntry, absence_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(absenceTypes).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date">Data Inizio *</Label>
                        <Input
                          id="start_date"
                          type="date"
                          value={absenceEntry.start_date}
                          onChange={(e) => setAbsenceEntry({ ...absenceEntry, start_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_date">Data Fine *</Label>
                        <Input
                          id="end_date"
                          type="date"
                          value={absenceEntry.end_date}
                          onChange={(e) => setAbsenceEntry({ ...absenceEntry, end_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reason">Motivo</Label>
                      <Textarea
                        id="reason"
                        value={absenceEntry.reason}
                        onChange={(e) => setAbsenceEntry({ ...absenceEntry, reason: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setAbsenceDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit">Salva</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Calendar View */}
                <div style={{ height: 600 }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    eventPropGetter={eventStyleGetter}
                    messages={{
                      next: 'Prossimo',
                      previous: 'Precedente',
                      today: 'Oggi',
                      month: 'Mese',
                      week: 'Settimana',
                      day: 'Giorno',
                      agenda: 'Agenda',
                      date: 'Data',
                      time: 'Ora',
                      event: 'Evento',
                    }}
                  />
                </div>

                {/* Pending Absences Table */}
                {hasRole(['ADMIN', 'SECRETARY']) && (
                  <div className="border-t pt-6">
                    <h3 className="font-heading font-semibold text-lg mb-4">Richieste in Attesa</h3>
                    {absences.filter(a => a.status === 'PENDING').length === 0 ? (
                      <p className="text-slate-600 text-center py-4">Nessuna richiesta in attesa</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-secondary border-b">
                            <tr>
                              <th className="text-left p-4 text-sm font-semibold text-slate-700">Utente</th>
                              <th className="text-left p-4 text-sm font-semibold text-slate-700">Tipo</th>
                              <th className="text-left p-4 text-sm font-semibold text-slate-700">Periodo</th>
                              <th className="text-left p-4 text-sm font-semibold text-slate-700">Motivo</th>
                              <th className="text-right p-4 text-sm font-semibold text-slate-700">Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {absences.filter(a => a.status === 'PENDING').map(absence => (
                              <tr key={absence.id} className="border-b hover:bg-secondary">
                                <td className="p-4">{getUserName(absence.user_id)}</td>
                                <td className="p-4">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium"
                                    style={{ backgroundColor: absenceColors[absence.absence_type] + '20', 
                                             color: absenceColors[absence.absence_type] }}>
                                    {absenceTypes[absence.absence_type]}
                                  </span>
                                </td>
                                <td className="p-4 text-slate-600">
                                  {formatDate(absence.start_date)} - {formatDate(absence.end_date)}
                                </td>
                                <td className="p-4 text-slate-600">{absence.reason || '-'}</td>
                                <td className="p-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleApproveAbsence(absence.id)}
                                      className="text-green-600 hover:bg-green-50"
                                    >
                                      <Check size={16} className="mr-1" />
                                      Approva
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRejectAbsence(absence.id)}
                                      className="text-red-600 hover:bg-red-50"
                                    >
                                      <X size={16} className="mr-1" />
                                      Rifiuta
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
